import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import toast from 'react-hot-toast';

// Clean, repaired banner component with robust translation fallback.
// It listens for the global 'subscription-required' CustomEvent dispatched by the Axios interceptor / dashboard logic.
// state: { subscriptionStatus, expiryDate, message? }

export default function SubscriptionBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [lastToastAt, setLastToastAt] = useState(0);
  const TOAST_COOLDOWN_MS = 4000;

  // Translation helper: never show raw key, always fallback string if missing
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    if (!value || value === key) return fallback;
    return value;
  }, [t]);

  useEffect(() => {
    const handler = (event) => {
      const { subscriptionStatus, expiryDate, message } = event.detail || {};
      setSubscriptionError({ subscriptionStatus, expiryDate, message });
      setDismissed(false);
      const now = Date.now();
      if (now - lastToastAt > TOAST_COOLDOWN_MS) {
        let toastKey;
        if (subscriptionStatus === 'NONE') toastKey = 'subscription.noActiveSubscription';
        else if (subscriptionStatus === 'EXPIRED') toastKey = 'subscription.expired.title';
        else toastKey = 'subscription.notActive';
        toast.error(tr(toastKey, 'Subscription issue'));
        setLastToastAt(now);
      }
    };

    window.addEventListener('subscription-required', handler);
    return () => window.removeEventListener('subscription-required', handler);
  }, [tr, lastToastAt]);

  if (!subscriptionError || dismissed) return null;

  const status = subscriptionError.subscriptionStatus;
  const headerKey = status === 'NONE'
    ? 'subscription.noActiveSubscription'
    : status === 'EXPIRED'
      ? 'subscription.expired.title'
      : 'subscription.notActive';

  const bodyText = status === 'NONE'
    ? tr('subscription.noActiveSubscription', 'No active subscription')
    : status === 'EXPIRED'
      ? tr('subscription.expired.message', 'Your subscription has expired.')
      : tr('subscription.notActive', 'Subscription not active');

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">{tr(headerKey, 'Subscription issue')}</p>
            <p className="text-sm opacity-90">{bodyText}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => {
              // Fire global event so Dashboard can mirror an internal billing tab click
              window.dispatchEvent(new CustomEvent('dashboard-switch-tab', { detail: { tab: 'billing', source: 'subscription-banner' }}));
              navigate('/dashboard?tab=billing');
            }}
            className="bg-white text-red-600 px-4 py-2 rounded font-medium hover:bg-red-50 transition-colors"
          >
            {tr('renewNow', 'Renew Now')}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-red-700 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
